import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import { useSearchParams } from 'react-router'
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { useToday } from '@/components/calendar/month-grid'
import { Button } from '@/components/ui/button'
import { ErrorState } from '@/components/ui/error-state'
import { cn } from '@/lib/utils'
import { SM_UP_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { usePortfolioWindows, type PortfolioWindowState } from '@/hooks/use-portfolio-calendar'
import { LayerToggleChip } from '@/components/property/calendar/layer-toggles'
import { CALENDAR_LAYERS } from '@/components/property/calendar/layers'
import {
  PORTFOLIO_LAYER_PREFS_KEY,
  useLayerPrefs,
} from '@/components/property/calendar/use-layer-prefs'
import { newBlockOpenValue } from '@/components/property/calendar/block-utils'
import { dayOpenValue } from '@/components/property/calendar/day-list'
import type { CalendarSheetNav } from '@/components/property/calendar/sheet-open'
import { buildDayAxis } from './day-axis'
import { createTipStore, type PortfolioItemActions } from './item-actions'
import { NARROW_DENSITY, WIDE_DENSITY, buildPortfolioRows, countPortfolioLayers } from './layout'
import { PortfolioLegend } from './portfolio-legend'
import { withoutItems, type PortfolioProperty } from './merge'
import { PortfolioGrid, PortfolioGridSkeleton, type GridGap } from './portfolio-grid'
import { BlockSettleWatch, PortfolioSheets } from './portfolio-sheets'
import { PortfolioTooltip } from './portfolio-tooltip'
import { sheetFor, sheetReadsBlocks } from './sheet-calendar'
import { useVisibleDays } from './use-visible-days'
import {
  WINDOW_DAYS,
  dayIso,
  dayNumber,
  dayOfDate,
  nextWindowSpan,
  rangeLabel,
  visibleWindows,
  wantedWindows,
  windowStartDay,
  type WindowSpan,
} from './windows'

/** On load (and on Today), today sits this many days in from the left edge, with the days just past beside it. */
export const DAYS_BEFORE_TODAY = 2

/** The left-edge day reaches the URL this long after scrolling settles: a history entry per day scrolled would be noise. */
export const URL_SETTLE_MS = 300

/** Today scrolls there smoothly when it is at most this many days away; further, it jumps. */
const SMOOTH_JUMP_DAYS = 31

/** A glide that has not arrived by then (the reader scrolled away) is let go. */
const GLIDE_TIMEOUT_MS = 1500

/** How long after reaching its day a glide keeps its days pinned, for its last pixels. */
const GLIDE_SETTLE_MS = 250

/**
 * The open sheet's place in the URL (CAP-1418), beside `?from=`:
 * `?sheet=stay:<id>&property=<id>`, with `section` (where in a stay's sheet)
 * and `back` (the day list a block's sheet was reached from) riding along. The
 * sheet values are the property tab's own `?open=` values.
 */
export const SHEET_PARAM = 'sheet'
export const SHEET_PROPERTY_PARAM = 'property'
const SECTION_PARAM = 'section'
const BACK_DAY_PARAM = 'back'
const SHEET_PARAMS = [SHEET_PARAM, SHEET_PROPERTY_PARAM, SECTION_PARAM, BACK_DAY_PARAM]

const KEY_STEPS: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  PageUp: -7,
  PageDown: 7,
}

/**
 * The portfolio calendar on `/` (CAP-1415, CAP-1416): every active property on
 * one timeline that scrolls without end, read 21 days at a time.
 *
 * This component owns the reads and their states, which days the grid spans,
 * and the URL; `PortfolioGrid` draws whatever rows and days it is handed. The
 * merge and the layout (lanes, positions) are built here once per (grid days,
 * day width, data version), never per scroll frame: a scroll frame only moves
 * the visible-day anchor, which re-renders once per day crossed.
 */
export function PortfolioCalendar() {
  const today = useToday()
  const todayDay = dayOfDate(today)
  const openingDay = todayDay - DAYS_BEFORE_TODAY
  const density = useMediaQuery(SM_UP_QUERY) ? WIDE_DENSITY : NARROW_DENSITY
  const { dayWidth, asideWidth } = density

  // `?from=` is the day at the left edge, so a reload or Back returns to it.
  const [searchParams, setSearchParams] = useSearchParams()
  const fromParam = searchParams.get('from')
  const [initialDay] = useState(() => dayNumber(fromParam) ?? openingDay)

  // The windows the grid spans. It follows the days on screen (see
  // `nextWindowSpan`): grown when they near an end, trimmed when windows fall
  // far behind. Adjusted while rendering, so the new days and the scroll
  // correction for them land in the same frame.
  const [span, setSpan] = useState<WindowSpan>(() => nextWindowSpan(null, initialDay, initialDay))
  const axisStart = windowStartDay(span.first)
  const axisEnd = windowStartDay(span.last + 1) - 1
  const axis = useMemo(() => buildDayAxis(dayIso(axisStart), dayIso(axisEnd)), [axisStart, axisEnd])

  // The grid's one tooltip. A scroll drops it: the item under it moves away
  // without a pointer leave.
  const [tips] = useState(createTipStore)
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const visible = useVisibleDays(element, {
    dayWidth,
    asideWidth,
    axisStart,
    dayCount: axis.days.length,
    initialDay,
    onScroll: tips.hide,
  })
  const firstDay = visible.firstVisibleDay
  const lastDay = firstDay + Math.max(visible.visibleCount, 1) - 1

  // While the view glides to a day (Today, a week back or on), the grid is
  // given every day of the way before it starts and keeps them until it
  // arrives: a window added or dropped on the left mid-way would move
  // `scrollLeft` and stop the glide short.
  const [glide, setGlide] = useState<{ from: number; to: number } | null>(null)
  const arrived = glide !== null && firstDay === glide.to
  useEffect(() => {
    if (glide === null) return undefined
    // Let go a moment after the left-edge day is the target, since the glide
    // may still be finishing the last pixels of it; and anyway after a while,
    // since a glide the reader cut short by scrolling never arrives.
    const timer = setTimeout(() => setGlide(null), arrived ? GLIDE_SETTLE_MS : GLIDE_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [glide, arrived])

  // From where the glide started to where it ends, whatever the screen shows
  // on the way, so no window is added or dropped until it arrives.
  const shownDays = lastDay - firstDay
  const spanFirst = glide === null ? firstDay : Math.min(firstDay, glide.from, glide.to)
  const spanLast =
    glide === null ? lastDay : Math.max(lastDay, glide.from + shownDays, glide.to + shownDays)
  const nextSpan = nextWindowSpan(span, spanFirst, spanLast)
  if (nextSpan !== span) setSpan(nextSpan)

  const { windows, properties, hasData } = usePortfolioWindows({
    span,
    visible: visibleWindows(firstDay, lastDay),
    wanted: wantedWindows(firstDay, lastDay),
  })

  // Once the grid has had rows, it stays: a jump to days not read yet (Today
  // from far away, a link) shows those days loading, or failed, inside the
  // grid, keeping the toolbar and the grid's focus. Until the new days answer,
  // the rows are the properties last read, with nothing drawn on them.
  const [lastRead, setLastRead] = useState<PortfolioProperty[] | null>(null)
  if (hasData && properties !== lastRead) setLastRead(properties)
  const unread = useMemo(() => (lastRead ?? []).map(withoutItems), [lastRead])
  const shownProperties = hasData ? properties : unread

  // Which layers are drawn: everything until the viewer switches one off, and
  // that choice kept in this browser as a display preference only.
  const { isVisible, toggle } = useLayerPrefs(PORTFOLIO_LAYER_PREFS_KEY)
  const { markersPerDay } = density
  // Laid out once per (properties read, grid days, layers on, markers a day
  // holds): lanes and the "+N" groups depend on which layers are on, never on
  // the scroll position.
  const rows = useMemo(
    () => buildPortfolioRows(shownProperties, axis, { isVisible, markersPerDay }),
    [shownProperties, axis, isVisible, markersPerDay]
  )
  // New rows may have moved or dropped the item a tooltip was shown for, with
  // no pointer leave to say so.
  useEffect(() => tips.hide(), [rows, tips])

  // Days with no answer: loading or failed, by window.
  const gaps = windows.flatMap((window): GridGap[] => {
    if (window.data) return []
    const start = windowStartDay(window.window) - axisStart
    const gap = {
      key: window.from,
      start,
      end: start + WINDOW_DAYS - 1,
      label: rangeLabel(start + axisStart, start + axisStart + WINDOW_DAYS - 1),
    }
    return window.error
      ? [{ ...gap, state: 'error', onRetry: window.refetch, retrying: window.isFetching }]
      : [{ ...gap, state: 'loading' }]
  })
  const onScreen = (window: PortfolioWindowState) =>
    windowStartDay(window.window) <= lastDay &&
    windowStartDay(window.window) + WINDOW_DAYS - 1 >= firstDay
  const busy = windows.some(window => window.isPending && onScreen(window))
  // Days in view with no answer, still being read or failed: no count is the
  // whole answer then.
  const unanswered = windows.some(window => !window.data && onScreen(window))

  // What each chip counts: the days in view, as the range label says them.
  // Recounted once per day scrolled, from the per-item content the layout
  // already made. While a window in view is still being read, no count: one
  // that may still change is not shown (the property tab's rule).
  const counts = useMemo(
    () => countPortfolioLayers(shownProperties, firstDay, lastDay),
    [shownProperties, firstDay, lastDay]
  )
  const stale = windows.filter(window => window.data && window.error)

  const { scrollToDay } = visible
  const goToDay = (day: number) => {
    const glides = scrollToDay(day, { smooth: Math.abs(day - firstDay) <= SMOOTH_JUMP_DAYS })
    setGlide(glides ? { from: firstDay, to: day } : null)
  }
  const goToday = () => goToDay(openingDay)
  // A week from where the view is heading, so two quick clicks go two weeks.
  const goByWeek = (weeks: number) => goToDay((glide?.to ?? firstDay) + 7 * weeks)

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    // Leave the browser's own shortcuts (Alt+Left is Back) alone.
    if (event.altKey || event.metaKey || event.ctrlKey) return
    const step = KEY_STEPS[event.key]
    if (step) {
      event.preventDefault()
      event.currentTarget.scrollLeft += step * dayWidth
    } else if (event.key === 'Home') {
      event.preventDefault()
      goToday()
    }
  }

  // The left-edge day into the URL once scrolling settles, replacing the
  // entry. At the opening day the URL stays bare, so tomorrow's reload opens
  // on tomorrow.
  const writtenRef = useRef(fromParam)
  const measured = visible.visibleCount > 0
  // What a screen reader hears: the days in view once scrolling settles, not
  // one announcement per day scrolled past.
  const [announced, setAnnounced] = useState('')
  useEffect(() => {
    if (!measured) return undefined
    const timer = setTimeout(() => {
      setAnnounced(`Showing ${rangeLabel(firstDay, lastDay)}`)
      const from = firstDay === openingDay ? null : dayIso(firstDay)
      if (from === writtenRef.current) return
      writtenRef.current = from
      setSearchParams(
        current => {
          const next = new URLSearchParams(current)
          if (from) next.set('from', from)
          else next.delete('from')
          return next
        },
        { replace: true }
      )
    }, URL_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [measured, firstDay, lastDay, openingDay, setSearchParams])

  // A `?from=` this page did not write (a link, the sidebar's Calendar entry)
  // moves the grid there, or to today when it is gone.
  useEffect(() => {
    if (fromParam === writtenRef.current) return
    writtenRef.current = fromParam
    scrollToDay(dayNumber(fromParam) ?? openingDay)
  }, [fromParam, scrollToDay, openingDay])

  // The sheet open over the grid, if any: the property tab's own sheets, for
  // the property in the URL. Opening one adds a history entry, so Back closes
  // it; closing it replaces that entry. The grid stays mounted underneath, so
  // it is where it was when the sheet closes.
  const sheetValue = searchParams.get(SHEET_PARAM)
  const sheetProperty = searchParams.get(SHEET_PROPERTY_PARAM)
  const sheetNav: CalendarSheetNav = {
    open: sheetValue,
    section: searchParams.get(SECTION_PARAM),
    fromDay: searchParams.get(BACK_DAY_PARAM),
    go: (value, { replace = false, fromDay, section } = {}) =>
      setSearchParams(
        current => {
          const next = new URLSearchParams(current)
          next.delete(SECTION_PARAM)
          next.delete(BACK_DAY_PARAM)
          if (value === null) {
            next.delete(SHEET_PARAM)
            next.delete(SHEET_PROPERTY_PARAM)
          } else {
            next.set(SHEET_PARAM, value)
            if (fromDay) next.set(BACK_DAY_PARAM, fromDay)
            if (section) next.set(SECTION_PARAM, section)
          }
          return next
        },
        { replace }
      ),
  }

  // What the grid's items and days do. One object for the life of the grid,
  // so the memoised rows never re-render for it; it reads the latest render's
  // values when it is used.
  const latest = useRef({ axis, firstDay, openingDay, measured, searchParams, setSearchParams })
  // The item a sheet was opened from, to give focus back to when it closes.
  const openerRef = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    latest.current = { axis, firstDay, openingDay, measured, searchParams, setSearchParams }
  })
  const actions = useMemo<PortfolioItemActions>(() => {
    const openSheet = (propertyId: string, value: string, section?: string) => {
      tips.hide()
      const { firstDay, openingDay, measured, searchParams, setSearchParams } = latest.current
      // The left-edge day goes into the current entry first, so Back from the
      // sheet finds `?from=` where the grid is, even when scrolling settled a
      // moment ago and its URL write is still waiting.
      const base = new URLSearchParams(searchParams)
      if (measured) {
        const from = firstDay === openingDay ? null : dayIso(firstDay)
        if (from) base.set('from', from)
        else base.delete('from')
        if (base.toString() !== searchParams.toString()) {
          writtenRef.current = from
          setSearchParams(base, { replace: true })
        }
      }
      const next = new URLSearchParams(base)
      SHEET_PARAMS.forEach(key => next.delete(key))
      next.set(SHEET_PROPERTY_PARAM, propertyId)
      next.set(SHEET_PARAM, value)
      if (section) next.set(SECTION_PARAM, section)
      setSearchParams(next)
    }
    return {
      tips,
      open: (propertyId, item, opener) => {
        const { value, section } = sheetFor(item)
        openerRef.current = opener
        openSheet(propertyId, value, section)
      },
      openDay: (propertyId, axisIndex, opener) => {
        const day = latest.current.axis.days[axisIndex]
        if (!day) return
        openerRef.current = opener
        openSheet(propertyId, dayOpenValue(day.iso))
      },
      openNewBlock: (propertyId, first, last, opener) => {
        openerRef.current = opener
        // The form's end is the day after the last night, as on the property tab.
        openSheet(propertyId, newBlockOpenValue(dayIso(first), dayIso(last + 1)))
      },
    }
  }, [tips])

  // Focus back where the sheet was opened from when it closes (Escape, its
  // close button, Back): the item, or the grid when the item is gone (a block
  // just released). The sheet's dialog hands focus back only to a trigger of
  // its own, which a sheet opened from a URL change does not have. Never
  // scrolled into view: the grid stays exactly where it was.
  const sheetOpen = !!(sheetValue && sheetProperty)
  const wasOpen = useRef(sheetOpen)
  useEffect(() => {
    if (wasOpen.current && !sheetOpen) {
      const opener = openerRef.current
      openerRef.current = null
      const active = document.activeElement
      if (!active || active === document.body) {
        const target = opener?.isConnected ? opener : element
        target?.focus({ preventScroll: true })
      }
    }
    wasOpen.current = sheetOpen
  }, [sheetOpen, element])

  // Each property whose blocks a sheet has read stays watched until none of
  // them is syncing, sheet open or not (see `BlockSettleWatch`).
  const blockSheetProperty = sheetOpen && sheetReadsBlocks(sheetValue) ? sheetProperty : null
  const [settling, setSettling] = useState<string[]>([])
  if (blockSheetProperty && !settling.includes(blockSheetProperty)) {
    setSettling([...settling, blockSheetProperty])
  }
  const letGo = useCallback(
    (propertyId: string) => setSettling(list => list.filter(id => id !== propertyId)),
    [setSettling]
  )

  const toolbar = (
    <div data-testid="portfolio-toolbar" className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="outline" size="sm" onClick={goToday}>
        Today
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9"
        aria-label="Back one week"
        onClick={() => goByWeek(-1)}
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9"
        aria-label="Forward one week"
        onClick={() => goByWeek(1)}
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Button>
      {measured && (
        <span
          data-testid="portfolio-range"
          className="px-1.5 text-sm font-semibold tabular-nums text-text"
        >
          {rangeLabel(firstDay, lastDay)}
        </span>
      )}
      <span aria-live="polite" className="sr-only">
        {announced}
      </span>
      <div
        role="group"
        aria-label="Calendar layers"
        data-testid="portfolio-layer-toggles"
        className={cn(
          'flex items-center gap-2 sm:ml-auto sm:flex-wrap',
          // On a phone the chips are one row that scrolls sideways, as in the
          // mock, rather than three rows pushing the grid down.
          'max-sm:-mx-4 max-sm:w-[calc(100%+2rem)] max-sm:overflow-x-auto max-sm:px-4 max-sm:[scrollbar-width:none]'
        )}
      >
        {CALENDAR_LAYERS.map(({ id, label }) => (
          <LayerToggleChip
            key={id}
            id={id}
            label={label}
            on={isVisible(id)}
            count={unanswered ? null : counts[id]}
            pending={busy ? 'layer' : null}
            readState={busy ? 'loading' : 'ready'}
            onToggle={toggle}
          />
        ))}
      </div>
    </div>
  )

  if (lastRead === null && !hasData) {
    const shown = windows.filter(onScreen)
    const failed = shown.length > 0 && shown.every(window => window.error && !window.isFetching)
    if (!failed) return <PortfolioGridSkeleton density={density} />
    // Nothing read, and the days on screen failed: said with a way to retry,
    // never drawn as an empty grid.
    return (
      <div className="rounded-card border border-border bg-surface">
        <ErrorState
          error={shown[0].error}
          title="Could not load the calendar"
          onRetry={() => shown.forEach(window => window.refetch())}
        />
      </div>
    )
  }

  return (
    // As tall as the space under the top bar and the page padding, at most:
    // the grid takes what the banner, toolbar and legend leave, so its header
    // sticks at its top and its horizontal scrollbar is on screen.
    <div className="flex max-h-[calc(100dvh-6rem)] flex-col md:max-h-[calc(100dvh-7rem)]">
      {stale.length > 0 && (
        // A read that failed after one succeeded keeps the last answer on
        // screen, grid or empty portfolio alike, but says it may be out of
        // date: a failure must never pass for a fresh answer.
        <div
          role="alert"
          data-testid="portfolio-refresh-error"
          className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-card border border-danger/50 bg-danger/10 px-4 py-2.5 text-sm text-danger"
        >
          <span className="inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            Could not refresh the calendar. What it shows may be out of date.
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={stale.some(window => window.isFetching)}
            onClick={() => stale.forEach(window => window.refetch())}
          >
            Retry
          </Button>
        </div>
      )}
      {shownProperties.length === 0 ? (
        <div
          data-testid="portfolio-empty"
          className="flex flex-col items-center justify-center rounded-card border border-border bg-surface px-4 py-16 text-center"
        >
          <CalendarDays className="mb-4 h-12 w-12 text-textMuted" aria-hidden="true" />
          <p className="text-sm font-medium text-text">No active properties to show</p>
          <p className="mt-1 max-w-sm text-sm text-textMuted">
            Each active property gets a row here, with everything on its calendar.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-3 space-y-2">
            {toolbar}
            {!density.narrow && <PortfolioLegend isVisible={isVisible} />}
          </div>
          <PortfolioGrid
            axis={axis}
            rows={rows}
            todayIndex={todayDay >= axisStart && todayDay <= axisEnd ? todayDay - axisStart : null}
            density={density}
            scrollRef={setElement}
            renderStart={visible.renderStart}
            renderEnd={visible.renderEnd}
            edge={measured ? firstDay - axisStart : null}
            visibleEnd={measured ? lastDay - axisStart : null}
            gaps={gaps}
            busy={busy}
            onKeyDown={onKeyDown}
            actions={actions}
          />
          <PortfolioTooltip store={tips} />
        </>
      )}
      {settling.map(propertyId => (
        <BlockSettleWatch
          key={propertyId}
          propertyId={propertyId}
          sheetOpen={propertyId === blockSheetProperty}
          onSettled={letGo}
        />
      ))}
      {sheetValue && sheetProperty && (
        <PortfolioSheets
          propertyId={sheetProperty}
          properties={properties}
          windows={windows}
          isVisible={isVisible}
          nav={sheetNav}
        />
      )}
    </div>
  )
}
